<template>
  <layout-list-detail :tableTools="toggleTableTools">
    <template slot="header">
      <BaseHeader :content="header" />
    </template>
    <template slot="main">
      <div class="form-wrapper">
        <el-form
          :model="form"
          :rules="rules"
          ref="ruleForm"
        >
          <div class="lg:mb-2" v-if="!creatingNew">
            <label class="text-sm font-semibold text-secondary-600 block mb-1">{{$t('grouping.groups_created')}}</label>
            <div class="flex justify-between">
              <el-form-item prop="groupingSelected" class="w-full">
                <el-select 
                  class="w-full" 
                  filterable 
                  v-model="form.groupingSelected" 
                  :placeholder="$t('grouping.select_group')"
                  clearable
                  @change="onChangeGroupingSelected()"
                  >
                  <el-option
                    v-for="grouping in groupings"
                    :key="grouping.idAgrupacion"
                    :label="grouping.Nombre"
                    :value="grouping.idAgrupacion">
                  </el-option>
                </el-select>
              </el-form-item>
            </div>
          </div>

          <div class="lg:mb-2" v-if="creatingNew">
            <div class="mandatory">
              <label class="text-sm font-semibold text-secondary-600 block mb-1"><base-obligatory-icon/>{{$t('app.name')}}</label>
              <el-form-item prop="nombre">
              <el-input
                clearable
                class="w-full"
                v-model="form.nombre"
                maxlength="100"
              />
              </el-form-item>
            </div>
          </div>
        </el-form>
    </div>
    </template>
    <template slot="table-header" v-if="selectedGroup && !creatingNew">
      <label class="text-sm font-semibold text-secondary-600 block mb-1">{{$t('grouping.companies_by_group')}}</label>
    </template>
    <template slot="table-core" v-if="selectedGroup && !creatingNew">
      <base-table-pagination
        :total="pagination.total"
        :currentPage="pagination.currentPage"
        :perPage="pagination.pageSize"
        @changeCurrentPage="changeCurrentPage"
        @changePerPage="changePerPage"
      ></base-table-pagination>
      <div class="flex flex-row w-100">
        <base-table-searches @toggleSearchesDialog="toggleSearchesDialog" />
        <base-table-search @toggleFilters="toggleFilters" />
      </div>
    </template>
    <template slot="table-tags" v-if="selectedGroup && !creatingNew">
      <base-table-filter-tags :tags="tags" @removeTag="removeFilterTagHanlder" @removeAllTags="removeAllTagsHandler" @saveTags="saveTagsHandler"/>
    </template>
    <template slot="table-component" v-if="selectedGroup && !creatingNew">
      <kendo-grid-component class="kendo-pointer-default"
        v-model="selected"
        value-key="IdCIFEmpresa"
        :id-vista="idVista"
        :spanish="spanish"
        :data="data"
        :kendo="kendo"
        :loading="isGeneralLoading"
        :selectedField="selectedField"
        @datastatechange="dataStateChange">
    </kendo-grid-component>
    </template>
    <template slot="layout-extend">
      <base-footer>
        <template slot="actions">
          <base-button-responsive v-if="creatingNew" mode="secondary" :text="$t('app.cancel')" icon="cross" @click="cancelCreating" :loading="isGeneralLoading"/>
          <base-button-responsive v-else mode="secondary" :text="$t('app.back')" icon="cross" @click="goBack" :loading="isGeneralLoading"/>

          <base-button-responsive v-if="creatingNew" mode="primary" :text="$t('app.save')" icon="check" @click="submitForm('ruleForm')" :loading="isGeneralLoading" :disabled="isGeneralLoading"/>

          <base-button-responsive v-if="selectedGroup && !creatingNew" mode="primary" :text="labelBtnExcel" icon="excelxls" @click="exportExcel" :loading="isGeneralLoading || loadingExcel" :disabled="loadingExcel"/>
          <base-button-responsive v-if="selectedGroup && !creatingNew" mode="primary" :text="$t('app.edit_name')" icon="editpencil" @click="toggleEditNameDialog" :loading="isGeneralLoading || loadingExcel" :disabled="isGeneralLoading"/>
          <base-button-responsive v-if="selectedGroup && !creatingNew" mode="primary" :text="$t('grouping.delete')" icon="trash" @click="toggleDeleteDialog" :loading="isGeneralLoading || loadingExcel" :disabled="isGeneralLoading"/>
          <base-button-responsive v-if="selectedGroup && !creatingNew" mode="primary" :text="$t('companies.link_company')" icon="link" @click="golinkGroupingCompanies" :loading="isGeneralLoading"/>
          <base-button-responsive v-if="!creatingNew" mode="primary" :text="$t('grouping.new_group')" icon="plus" @click="resetViewNew" :loading="isGeneralLoading"/>
        </template>
      </base-footer>
      <base-table-select-actions-expand
        :visibility="hasSelected"
        :selected="rowsSelected"
        :total="rowsTotal"
      >
        <template slot="commons">
          <base-button size="xs" mode="tools" @click="unselect">
            <base-icon name="vall" size="sm" class="mr-1" />
            {{$t('app.unselect')}}
          </base-button>
        </template>
        <template slot="more">
          <base-button-item-list theme="dark" @click="unlink">
            <base-icon name="unlink" class="mr-2" />
              {{$t('app.unlink_entity')}}
          </base-button-item-list>
        </template>
      </base-table-select-actions-expand>
      <Dialog
        :title="$t('grouping.delete')"
        :visible.sync="showModalDelete"
        width="40%"
        custom-class="min-width-dialog"
        center>
        <span class="text-center w-full inline-block dark:text-darkblue-200 word-break-n">{{ processMessage }}</span>
        <template slot="footer" >
          <div class="flex flex-col lg:flex-row w-full lg:w-auto items-center space-y-2 lg:space-y-0 lg:space-x-2">
            <base-button class="w-full" @click="showModalDelete = false" mode="secondary-phantom-blue" :text="$t('app.cancel')"/>
            <base-button class="w-full" mode="secondary-phantom-blue" :text="$t('app.accept')" @click="() => {deleteHandler(); showModalDelete = false}"/>
          </div>
        </template>
      </Dialog>
      <input-text-dialog 
        :dialogOpen="showModalEditName" 
        @toggleDialog="toggleEditNameDialog" 
        @applyDialog="applyEditName"
        :header="$t('grouping.link_to_group')"
        :headerValue="$t('grouping.new_group_name')" 
        />
      <template-link-Companies-dialog 
        :title="$t('companies.link_companies')"
        :dialogOpen="linkGroupingCompaniesDialog" 
        :clientId="$route.params.id" 
        @toggleDialog="toggleDialogs" 
        @applyDialog="applyLinkHandler" />
      <filter-panel :panelOpen="filterPanel" @togglePanel="toggleFilters" @applyFilters="applyFiltersHandler" />
      <create-search-dialog 
        :numberSearches="numberSearches"
        :idVista="idVista" 
        :dialogOpen="saveSearchDialog" 
        :tags="tags" 
        @toggleDialog="toggleDialogSearches" 
        @applyDialog="applySearchHandler" />
      <filter-panel-searches 
        ref="filterPanelSearches" 
        :idVista="idVista" 
        :vuexnamespace="vuexnamespace" 
        :panelOpen="searchesPanel" 
        @togglePanel="toggleSearchesDialog" 
        @applyFilters="applyFiltersHandler" 
        @updateNumberSearches="updateNumberSearches"/>
    </template>
  </layout-list-detail>
</template>

<script>
// import ListLayout from '@/components/layout/ListLayout.vue'
import { i18n } from '@/i18n'
import { Dialog } from 'element-ui'

import LayoutListDetail from '@/components/layout/DetailListLayout.vue'
import FilterPanel from '@/components/filters/CompaniesByGroupingFilter.vue'
import FilterPanelSearches from '@/components/searches/SearchesPanel.vue'
import CreateSearchDialog from '@/components/searches/CreateSearchDialog.vue'
import KendoGridComponent from '@/components/KendoGridComponentV2.vue'
import TemplateLinkCompaniesDialog from '@/components/TemplateLinkCompaniesDialog.vue'
import InputTextDialog from '@/components/InputTextDialog.vue'

import { getGroupingCompaniesPag } from '@/services/configurations/grouping/getGroupingCompaniesPag'
import { downloadGroupingCompaniesExcel } from '@/services/configurations/grouping/downloadGroupingCompaniesExcel'

import { linkGroupingCompanies } from '@/services/configurations/grouping/linkGroupingCompanies'
import { unlinkGroupingCompanies } from '@/services/configurations/grouping/unlinkGroupingCompanies'

import { newGroupingCompanies } from '@/services/configurations/grouping/newGroupingCompanies'
import { updateGroupingCompanies } from '@/services/configurations/grouping/updateGroupingCompanies'
import { deleteGroupingCompanies } from '@/services/configurations/grouping/deleteGroupingCompanies'
import { getAgrupacionesSimpleCompanies } from '@/services/configurations/grouping/getAgrupacionesSimpleCompanies'

export default {
  /* eslint-disable camelcase */
  name: 'grouping-companies-view',
  components: {
    // ListLayout,
    LayoutListDetail,
    FilterPanel,
    FilterPanelSearches,
    CreateSearchDialog,
    KendoGridComponent,
    Dialog,
    InputTextDialog,
    TemplateLinkCompaniesDialog
  },
  data: function () {
    return {
      idVista: 73,
      creatingNew: false,
      // Searches
      numberSearches: 0,
      saveSearchDialog: false,
      searchesPanel: false,
      // All
      showModalDelete: false,
      showModalEditName: false,
      loadingExcel: false,
      linkGroupingCompaniesDialog: false,
      searchView: false,
      spanish: true,
      header: {
        title: i18n.t('grouping.header_long'),
        icon: 'config',
        breadcrumb: null,
        url: 'configurations'
      },
      vuexnamespace: 'groupingcompanies',
      selectedField: 'selected',
      filterPanel: false,
      loadingDataTable: false,
      data: null,
      groupings: [],
      selected: [],
      form: {
        nombre: '',
        groupingSelected: null,
      },
      rules: {
        nombre: [
          {
            required: true,
            message: i18n.t('app.fill_data'),
            trigger: 'blur'
          }
        ]
      },
      processMessage: i18n.t('app.delete_current_confirmation_f', { entity: i18n.t('grouping.entity_name').toLowerCase() })
    }
  },
  computed: {
    selectedGroup () {
      return this.form.groupingSelected
    },
    labelBtnExcel: function () {
      if (this.hasSelected) return `${i18n.t('app.export_excel')} (${i18n.t('app.selected')})`
      return i18n.t('app.export_excel')
    },
    kendo: function () {
      return this.$store.getters[this.vuexnamespace + '/getKendo']
    },
    role: function () {
      return this.$store.getters['auth/role']
    },
    isHeadCoreReady: function () {
      return this.$store.getters.isHeadCoreReady
    },
    toggleTableTools: function () {
      return this.selected.length > 0
    },
    rowsSelected: function () {
      return this.selected.length
    },
    rowsTotal: function () {
      return this.selected.length
    },
    isGeneralLoading: function () {
      return this.$store.getters.isLoading
    },
    headerCoreFilters: function () {
      return this.$store.getters['auth/queryHeaders']
    },
    tags: function () {
      return this.$store.getters[this.vuexnamespace + '/getTags']
    },
    pagination: function () {
      return this.$store.getters[this.vuexnamespace + '/getPaginationInfo']
    },
    areAllSelected () {
      return this.data.length === this.selected.length
    },
    hasSelected () {
      return this.selected.length > 0
    },
    clienteId () {
      return this.$route.params.id ? parseInt(this.$route.params.id) : null
    },
    isSuperAdminView () {
      return !!this.clienteId
    },
    paramClientId () {
      return this.isSuperAdminView ? { idCliente: this.clienteId } : null
    }
  },
  watch: {
    headerCoreFilters: {
      // immediate: true,
      deep: true,
      handler: 'headerCoreFiltersHandler'
    }
  },
  created: function () {
  },
  mounted: async function () {
    const breadcrumbs = []
    if (this.isSuperAdminView) {
      breadcrumbs.push({
        path: 'configurations/clients/details',
        name: i18n.t('clients.entity_name')
      })
    } else {
      breadcrumbs.push({
        path: 'configurations',
        name: i18n.t('configurations.header')
      })
    }

    await this.downloadData()
    breadcrumbs.push({ 
      path: this.isSuperAdminView ? 'configurations/grouping/companies' : 'configurations/grouping/companies/main', 
      name: i18n.tc('grouping.grouping_companies', 2) 
    })
    this.header.url = this.isSuperAdminView ? 'configurations/clients/details' : 'configurations'
    this.header.breadcrumb = breadcrumbs
    this.$store.commit(this.vuexnamespace + '/setPageSize', this.kendo.take)
    if (this.selectedGroup) await this.applyFilters()
  },
  methods: {
    async onChangeGroupingSelected () {
      if (this.selectedGroup) await this.applyFilters()
    },
    toggleEditNameDialog () {
      this.showModalEditName = !this.showModalEditName
    },
    resetViewNew () {
      this.creatingNew = true
      // this.form.groupingSelected = null
    },
    cancelCreating () {
      this.creatingNew = false
      this.resetFormVal()
      this.downloadData()
    },
    downloadData: async function (idAgrupToSelect = null) {
      try {
        this.$store.commit('setIsLoading', true)
        const res = await getAgrupacionesSimpleCompanies(this.paramClientId)
        if (res.blnError === true) {
          this.doFeedback(i18n.t('feedback.error.formated_api_response_error', res))
        } else {
          this.groupings = res.Respuesta
          // Preseleccionamos el primero
          if (res.Respuesta.length > 0) {
            this.form.groupingSelected = idAgrupToSelect || res.Respuesta[0].idAgrupacion
            this.onChangeGroupingSelected()
          } else {
            this.form.groupingSelected = null
          }
        }
      } catch (error) {
        this.doFeedback()
      } finally {
        this.$store.commit('setIsLoading', false)
      }
    },
    toggleSearchView: function () {
      this.searchView = !this.searchView
    },
    submitForm: function (formName) {
      this.newGroupingCompaniesHandler(formName)
    },
    resetForm: function () {
      this.form.nombre = ''
    },
    newGroupingCompaniesHandler: function (formName) {
      this.$store.commit('setAjaxRequestPending', true)
      this.$store.commit('setIsLoading', true)
      this.$refs[formName].validate(async valid => {
        if (valid) {
          const params = {
            Nombre: this.form.nombre,
            idCliente: this.clienteId
          }
          const response = await newGroupingCompanies(params)
          if (response.blnError) {
            this.doFeedback(i18n.t('feedback.error.formated_api_response_error', response))
          } else {
            this.doFeedback(i18n.t('feedback.success.entity_created_msg'), 'success')
            this.redirectAfterCreating(response.Respuesta)
          }
        } else {
          this.doFeedback(i18n.t('feedback.error.process_data_error'))
        }
        this.$store.commit('setIsLoading', false)
      })
    },
    redirectAfterCreating (idNewGrouping) {
      this.creatingNew = false
      this.resetForm()
      this.resetFormVal()
      this.downloadData(idNewGrouping)
    },
    resetFormVal () {
      this.$refs.ruleForm.resetFields()
    },
    applyEditName: async function (nombre) {
      try {
        this.$store.commit('setIsLoading', true)
        const params = {
          NombreNuevo: nombre,
          idAgrupacion: this.form.groupingSelected,
          idCliente: this.clienteId
        }
        const response = await updateGroupingCompanies(params)
        if (response.blnError === true) {
          this.doFeedback(i18n.t('feedback.error.formated_api_response_error', response))
        } else {
          this.doFeedback(i18n.t('feedback.success.entity_updated_msg'), 'success')
          this.toggleEditNameDialog()
          this.downloadData(this.form.groupingSelected)
        }
      } catch (error) {
        this.doFeedback()
      } finally {
        this.$store.commit('setIsLoading', false)
      }
    },
    toggleDeleteDialog () {
      this.showModalDelete = !this.showModalDelete
    },
    deleteHandler: async function () {
      try {
        this.$store.commit('setIsLoading', true)
        const params = {
          idAgrupacion: this.form.groupingSelected,
        }
        const response = await deleteGroupingCompanies(params)
        if (response.blnError === true) {
          this.doFeedback(i18n.t('feedback.error.formated_api_response_error', response))
        } else {
          this.doFeedback(i18n.t('feedback.success.entity_deleted_msg'), 'success')
          this.unselect()
          this.downloadData()
        }
      } catch (error) {
        this.doFeedback()
      } finally {
        this.$store.commit('setIsLoading', false)
      }
    },
    goBack: function () {
      if (this.isSuperAdminView) this.$router.push({ name: 'configurations/clients/details' })
      else this.$router.push({ name: 'configurations' })
    },
    toggleFilters: function () {
      this.filterPanel = !this.filterPanel
    },
    toggleDialogs: function () {
      this.linkGroupingCompaniesDialog = !this.linkGroupingCompaniesDialog
    },
    dataStateChange: async function (event) {
      this.data = await this.applyFilters()
    },
    exportExcel: async function () {
      this.$store.commit('app/setLoading', true)
      this.loadingExcel = true
      const data = this.$store.getters[this.vuexnamespace + '/getAppliedFilters']
      const params = {
        idAgrupacion: this.selectedGroup,
        CIF: data.cif ? data.cif.value : null,
        razonSocial: data.name ? data.name.value : null,
        fechaAltaDesde: data.fechaAltaDesde ? data.fechaAltaDesde.value : null,
        fechaAltaHasta: data.fechaAltaHasta ? data.fechaAltaHasta.value : null,
        estado: data.estado ? data.estado.value : null,
        email: data.email ? data.email.value : null,
        enviarAvisos: data.send_alerts ? data.send_alerts.value : null,
        certAsociado: data.certAsociado ? data.certAsociado.value : null,
        nombreAgrupacion: data.nombreAgrupacion ? data.nombreAgrupacion.value : null,
        firmaDelegada: data.firmaDelegada ? data.firmaDelegada.value : null,
      }
      const objOrder = this.getObjOrderMixin(this.kendo.group, this.kendo.sort)
      const objColumnas = this.getColumnsGroupExcelMixin(this.kendo.columns, this.kendo.group)
      const payload = { order: objOrder.order, grupos: objColumnas }
      if (data.ActiveReports) {
        payload.ActiveReports = data.ActiveReports.value
      }
      if (this.hasSelected) {
        const selectedIDs = this.selected.map(x => x.IdTag)
        payload.listaIDs = selectedIDs
      }
      try {
        const response = await downloadGroupingCompaniesExcel(payload, params)
        const blob = new Blob([response.data], { type: response.data.type, encoding: 'UTF-8' })
        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(blob)
        let filename = 'agrupacion-empresas.xlsx'
        filename = decodeURI(filename)
        link.download = filename
        link.click()
      } catch (error) {
        console.error(error)
        if (error.response?.data) {
          const errorText = await error.response.data.text()
          this.doFeedback(errorText)
        } else {
          this.doFeedback()
        }
      } finally {
        this.$store.commit('app/setLoading', false)
        this.loadingExcel = false
      }
    },
    applyFilters: async function (eventData) {
      this.loadingDataTable = true
      const data = this.$store.getters[this.vuexnamespace + '/getAppliedFilters']
      const params = {
        idAgrupacion: this.selectedGroup,
        CIF: data.cif ? data.cif.value : null,
        razonSocial: data.name ? data.name.value : null,
        fechaAltaDesde: data.fechaAltaDesde ? data.fechaAltaDesde.value : null,
        fechaAltaHasta: data.fechaAltaHasta ? data.fechaAltaHasta.value : null,
        estado: data.estado ? data.estado.value : null,
        email: data.email ? data.email.value : null,
        enviarAvisos: data.send_alerts ? data.send_alerts.value : null,
        certAsociado: data.certAsociado ? data.certAsociado.value : null,
        nombreAgrupacion: data.nombreAgrupacion ? data.nombreAgrupacion.value : null,
        firmaDelegada: data.firmaDelegada ? data.firmaDelegada.value : null,
      }
      const objOrder = this.getObjOrderMixin(this.kendo.group, this.kendo.sort)
      if (data.ActiveReports) {
        objOrder.ActiveReports = data.ActiveReports.value
      }
      try {
        this.data = await this.getItemsData(objOrder, params)
      } catch (error) {
        this.$store.commit('setIsLoading', false)
        this.doFeedback()
      }
      return this.data
    },
    getItemsData: async function (objOrder, filters) {
      this.$store.commit('setAjaxRequestPending', true)
      this.$store.commit('setIsLoading', true)
      const params = {
        paginate: true,
        results: this.pagination.pageSize,
        page: this.pagination.currentPage,
        ...filters
      }
      const res = await this.getMainData(objOrder, params)
      this.data = res.respuesta
      const pagination = {
        total: res.meta?.total,
        currentPage: res.meta?.currentPage,
        totalPages: res.meta?.lastPage,
        pageSize: res.meta?.perPage
      }
      
      this.$store.commit(this.vuexnamespace + '/setPaginationInfo', pagination)
      this.$store.commit('setIsLoading', false)
      this.loadingDataTable = false
      return this.data
    },
    async getMainData (objOrder, filters) {
      const params = {
        paginate: true,
        results: this.pagination.pageSize,
        page: this.pagination.currentPage,
        ...filters
      }
      const data = await getGroupingCompaniesPag(objOrder, params)
      if (data.blnError === true) {
        this.doFeedback(i18n.t('feedback.error.formated_api_response_error', data))
      }
      data.respuesta.forEach(o => {
        o.DiasDescargaAutomatica = o.DiasDescargaAutomatica === null ? 0 : o.DiasDescargaAutomatica
        o.DiasAviso = o.DiasAviso === null ? 0 : o.DiasAviso
        o.DiasFinTramite = o.DiasFinTramite === null ? 0 : o.DiasFinTramite
      })
      return data
    },
    applyFiltersHandler: async function (eventData) {
      this.unselect()
      this.$store.commit(this.vuexnamespace + '/setCurrentPage', 1)
      this.data = await this.applyFilters(eventData)
    },
    changeCurrentPage: async function (event) {
      this.$store.commit(this.vuexnamespace + '/setCurrentPage', event)
      this.data = await this.applyFilters(null)
    },
    changePerPage: async function (event) {
      this.$store.commit(this.vuexnamespace + '/setCurrentPage', 1)
      this.$store.commit(this.vuexnamespace + '/setPageSize', event)
      this.kendo.take = event
      this.data = await this.applyFilters(null)
    },
    removeFilterTagHanlder: function (tag) {
      this.unselect()
      // this.$store.commit(this.vuexnamespace + '/removeTag', tag)
      this.$store.commit(this.vuexnamespace + '/setCurrentPage', 1)
      const filter = this.$store.getters[this.vuexnamespace + '/getAppliedFilters']
      delete filter[tag[0]]
      this.$store.commit(this.vuexnamespace + '/setMasterFilters', filter)
      this.applyFiltersHandler(null)
    },
    headerCoreFiltersHandler: function (obs) {
      if (this.isHeadCoreReady) {
        this.applyFilters(null)
      }
    },
    removeAllTagsHandler: function () {
      this.unselect()
      this.$store.commit(this.vuexnamespace + '/setCurrentPage', 1)
      this.$store.commit(this.vuexnamespace + '/resetMasterFilters')
      this.applyFiltersHandler(null)
    },
    unselect: function () {
      this.selected = []
    },
    unlink: async function () {
      const selected = this.selected
      await this.unlinkHandler(selected)

      this.data = await this.applyFilters()
    },
    unlinkHandler: async function (empresas) {
      try {
        this.$store.commit('setIsLoading', true)
        const payload = { idAgrupacion: this.selectedGroup, idCifEmpresas: this.selected }
        const response = await unlinkGroupingCompanies(payload)
        if (response.blnError === true) {
          this.doFeedback(i18n.t('feedback.error.formated_api_response_error', response))
        } else {
          this.unselect()
          this.doFeedback(i18n.t('feedback.success.entity_unlinked_msg'), 'success')
        }
      } catch (error) {
        this.doFeedback()
      } finally {
        this.$store.commit('setIsLoading', false)
      }
    },
    golinkGroupingCompanies: function () {
      this.linkGroupingCompaniesDialog = !this.linkGroupingCompaniesDialog
    },
    applyLinkHandler: async function (companies) {
      this.linkGroupingCompanies = false
      await this.linkHandler(companies)
      this.applyFilters()
    },
    linkHandler: async function (empresas) {
      try {
        this.$store.commit('setIsLoading', true)
        const payload = { idAgrupacion: this.form.groupingSelected, idCIFEmpresas: empresas }
        const response = await linkGroupingCompanies(payload)
        if (response.blnError === true) {
          this.doFeedback(i18n.t('feedback.error.formated_api_response_error', response))
        } else {
          this.doFeedback(i18n.t('feedback.success.entity_linked_msg'), 'success')
          this.golinkGroupingCompanies()
        }
      } catch (error) {
        this.doFeedback()
      } finally {
        this.$store.commit('setIsLoading', false)
      }
    },
    toggleSearchesDialog: function () {
      this.searchesPanel = !this.searchesPanel
    },
    saveTagsHandler: function () {
      this.saveSearchDialog = !this.saveSearchDialog
    },
    toggleDialogSearches: function () {
      this.saveSearchDialog = !this.saveSearchDialog
    },
    applySearchHandler: async function () {
      this.toggleDialogSearches()
      // Manda actualizar lista de búsquedas
      this.$refs.filterPanelSearches.updateData()
    },
    updateNumberSearches: async function (number) {
      this.numberSearches = number
    },
  }
}
</script>
